import { CommonModule } from "@angular/common";
import { ConnectedPosition, OverlayModule } from "@angular/cdk/overlay";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  input,
  output,
  signal,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { ButtonComponent, EnrTooltipDirective } from "@corp-ais/omniark-ui";
import type { LanguageModeValue } from "@app/shared";
import type { ActiveLanguage } from "@app/shared/services/language-config.store";
import {
  PACKAGE_STATUS_HINT,
  PACKAGE_TONE_CLASS,
  PackageStatusView,
} from "@app/shared/models/package-status.model";
import {
  PRICE_TONE_CLASS,
  priceStatusView,
} from "@app/shared/models/price-status.model";
import { formatFigures } from "../../../utils/price-amount.util";
import type { PriceEntry } from "../../../utils/price-entry.util";
import type { ReadinessTone } from "../enrichment-edit-package/active-price-readiness";
import { PriceSwitcher } from "../price-switcher/price-switcher";
import type {
  FlatLangCoverage,
  MissingField,
} from "../enrichment-edit-package/flat-language-coverage.util";

export interface HeroPackage {
  readonly id: string;
  readonly name: string;
  readonly status?: PackageStatusView;
}

/**
 * Hero band shown at the top of the price editor.
 *
 * Owns the price's identity (title, status on this channel, POP id, product
 * code, amount incl. VAT and period), the package context line with the
 * sibling switcher, the readiness donut, the per-language coverage and the
 * primary actions (Cancel / Publish this price). Stateless / dumb — all data
 * is provided via inputs; user interactions are emitted as outputs for the
 * shell to handle.
 */
@Component({
  selector: "app-enrichment-edit-package-hero",
  standalone: true,
  imports: [
    CommonModule,
    OverlayModule,
    MatIconModule,
    ButtonComponent,
    EnrTooltipDirective,
    PriceSwitcher,
  ],
  templateUrl: "./enrichment-edit-package-hero.html",
  styleUrls: ["./enrichment-edit-package-hero.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnrichmentEditPackageHero {
  readonly entry = input<PriceEntry | null>(null);
  readonly entries = input<readonly PriceEntry[]>([]);
  readonly pkg = input<HeroPackage | null>(null);
  readonly channelLabel = input("");
  /** Disables the switcher while a save is in flight. */
  readonly switching = input(false);

  /** Loading state — disables actions while the package is in flight. */
  @Input() loading: boolean = false;

  /** Live preview is open (narrow widths only — desktop always shows it). */
  @Input() previewOpen: boolean = false;

  /** Readiness summary used to surface "X to fix / Y to review". Optional. */
  @Input() readinessLabel: string | null = null;
  readonly readinessTone = input<ReadinessTone>("block");
  @Input() canPublish: boolean = false;

  /** True while the price is staged (`designed`) — the only state publish
   *  can transition. Lets the tooltip say WHY publish is off: nothing staged
   *  is a different fix ("edit and Save") than readiness blockers ("open the
   *  panel"). Defaults true so callers that don't bind it keep the
   *  blockers-only wording. */
  @Input() hasPublishablePrice: boolean = true;

  /** True when the package's own lifecycle is `retired` / `obsolete` — CPC
   *  has closed it, so Publish is off for good rather than pending a fix.
   *  Kept separate from `canPublish` so the tooltip can say that instead of
   *  sending the user to a readiness panel that has nothing to fix. */
  @Input() packageClosed: boolean = false;

  /** Whether Reject is offered in the Publish split menu. The shell ANDs two
   *  independent things into this: the price is on sale (`active`/`launched`)
   *  AND the user holds `enrichment:reject`. Deliberately NOT derived from
   *  `canPublish` — a live price with blocking edits is exactly the one a
   *  reviewer most wants to pull off sale. */
  @Input() canReject: boolean = false;

  /** Whether the user holds `enrichment:publish`.
   *
   *  Separate from `canPublish`, which answers "is this price publishable
   *  right now" — that one greys the button and explains itself. Permission is
   *  all-or-nothing per user, so there is no state they could reach that would
   *  make a greyed Publish informative: without it the control is not rendered
   *  at all, matching how Reject and the list's row actions behave.
   *
   *  Reject survives on its own when this is false — it is gated on its own
   *  permission and hangs off the split only for layout. */
  @Input() mayPublish: boolean = true;

  /** Readiness donut inputs. */
  @Input() readinessPercent: number = 0;
  @Input() readinessComplete: number = 0;
  @Input() readinessTotal: number = 0;

  /** Save lifecycle shown beside the actions: idle/dirty/saving/
   *  saved-relative-time. */
  @Input() saveState: "idle" | "dirty" | "saving" | "saved" = "idle";
  @Input() lastSavedAt: number | null = null;
  /** Updated by an external ticker so "Saved N min ago" advances over time. */
  @Input() relativeNow: number = Date.now();

  /** Active editing language. */
  readonly activeLanguage = input<LanguageModeValue>("ALL");

  /** Display-normalized active code. The service stores the Burmese locale as
   *  the legacy internal value "MY", but the UI labels its card "MM". Normalize
   *  here so the template's `activeDisplay() === lang.code` comparison matches
   *  and the selected state (color + aria-checked) actually toggles. */
  readonly activeDisplay = computed<LanguageModeValue>(() => {
    const v = this.activeLanguage();
    return v === "MY" ? "MM" : v;
  });

  /** True when the editor is filtered to a single language (not "ALL").
   *  Drives the grid's `--filtered` class which dims the non-selected
   *  cards so the focus card visually pops. */
  readonly hasLanguageFilter = computed<boolean>(
    () => this.activeDisplay() !== "ALL",
  );

  /** Active authoring-language set, sourced from the channel's linked
   *  languages. Read-only here — there is no add/remove-language flow.
   *  Drives the per-language coverage cards. */
  readonly activeLanguages = input<ActiveLanguage[]>([]);

  readonly coverage = input<Record<string, FlatLangCoverage>>({});

  readonly figuresText = computed(() => {
    const entry = this.entry();
    return entry ? formatFigures(entry.figures) : "";
  });

  readonly priceStatus = computed(() => {
    const chip = this.entry()?.status;
    const view = priceStatusView(chip?.type, this.channelLabel());
    return view && !view.status && chip ? { ...view, label: chip.label } : view;
  });
  readonly priceToneClass = PRICE_TONE_CLASS;
  readonly packageToneClass = PACKAGE_TONE_CLASS;
  readonly packageStatusHint = PACKAGE_STATUS_HINT;
  readonly readinessToneClass: Record<ReadinessTone, string> = {
    ready: "text-[#466b12]",
    warn: "text-[#92660d]",
    block: "text-[#b23a30]",
  };

  readonly copied = signal<string | null>(null);
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  copy(text: string): void {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    void navigator.clipboard.writeText(text).then(() => {
      this.copied.set(text);
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.copied.set(null), 1500);
    });
  }

  /** Safe lookup — empty entry when the locale isn't computed yet. */
  cov(locale: string): FlatLangCoverage {
    return (
      this.coverage()[locale] ?? {
        locale,
        filled: 0,
        total: 0,
        percent: 0,
        missing: [],
      }
    );
  }

  /** Which language card's "unfilled fields" popover is open (one at a time),
   *  or null when none is. */
  private readonly openMissingLocale = signal<string | null>(null);
  isMissingOpen(locale: string): boolean {
    return this.openMissingLocale() === locale;
  }
  toggleMissing(locale: string): void {
    this.openMissingLocale.update((cur) => (cur === locale ? null : locale));
  }
  closeMissing(): void {
    this.openMissingLocale.set(null);
  }
  /** A blank field was clicked — close the popover and ask the shell to jump. */
  onMissingClick(field: MissingField): void {
    this.closeMissing();
    this.fieldJump.emit(field);
  }

  /** Anchor the unfilled-fields popover below the toggle, flipping above when
   *  there's no room. */
  readonly missingPopoverPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 6,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -6,
    },
  ];

  // ── Publish split menu ────────────────────────────────────────────────────
  /** Whether the caret's menu is showing. */
  readonly publishMenuOpen = signal(false);

  togglePublishMenu(): void {
    this.publishMenuOpen.update((open) => !open);
  }

  closePublishMenu(): void {
    this.publishMenuOpen.set(false);
  }

  /** Menu item picked — close first so the confirm modal the shell opens isn't
   *  stacked underneath an overlay that is about to be torn down. */
  onRejectClick(): void {
    this.closePublishMenu();
    this.rejectClick.emit();
  }

  /** Anchored to the split button's right edge so a wide menu grows inward
   *  rather than off the viewport, flipping above when there's no room below. */
  readonly publishMenuPositions: ConnectedPosition[] = [
    {
      originX: "end",
      originY: "bottom",
      overlayX: "end",
      overlayY: "top",
      offsetY: 6,
    },
    {
      originX: "end",
      originY: "top",
      overlayX: "end",
      overlayY: "bottom",
      offsetY: -6,
    },
  ];

  /** Tooltip on the split button. A getter, not a `computed` — its inputs are
   *  plain `@Input()` fields rather than signals, so a computed would cache the
   *  first answer and never see Publish become available. Has to read sensibly
   *  in every state, since a disabled button is exactly when it's needed. */
  get publishHint(): string {
    // A closed package is the only permanent stop — no edit, save or fix
    // reopens it — so it outranks every other reason.
    if (this.packageClosed) {
      return "This package is retired or obsolete — it can no longer be published.";
    }
    // Nothing staged is the harder stop — fixing blockers alone won't light
    // the button when the price is already live or was never saved, so say
    // so first.
    if (!this.hasPublishablePrice) {
      return "This price has nothing staged to publish — edit and Save to stage a version first.";
    }
    if (!this.canPublish) {
      return "Publish is unavailable while this price has blocking issues — open the readiness panel to see what's missing.";
    }
    if (this.saveState === "dirty") {
      return "Save your changes before publishing.";
    }
    return "Publish this price to the selected channel.";
  }

  // ── Outputs ───────────────────────────────────────────────────────────────
  @Output() languageSelect = new EventEmitter<LanguageModeValue>();
  /** Cancel action — bail out of the edit. Renamed away from `cancel`
   *  to dodge the native DOM `cancel` event (@angular-eslint warning). */
  @Output() cancelClick = new EventEmitter<void>();
  /** Publish action — opens the review drawer, then advances the channel
   *  overlay `designed → active` (drawer-mediated). Always shown. */
  @Output() publishClick = new EventEmitter<void>();
  /** Reject action from the split menu — the shell confirms, then takes the
   *  price off sale for this channel (`active`/`launched` → `rejected`). */
  @Output() rejectClick = new EventEmitter<void>();
  @Output() togglePreview = new EventEmitter<void>();
  /** A blank localized field was clicked in a coverage card — the shell opens
   *  its tab and scrolls to it. */
  @Output() fieldJump = new EventEmitter<MissingField>();
  readonly priceSelect = output<string>();
  readonly openPackageSettings = output<void>();

  // ── Donut geometry (SVG circle) ───────────────────────────────────────────
  readonly donutSize = 56;
  readonly donutStrokeWidth = 6;
  readonly donutRadius = (this.donutSize - this.donutStrokeWidth) / 2;
  readonly donutCircumference = computed(() => 2 * Math.PI * this.donutRadius);
  readonly donutOffset = computed(() => {
    const pct = Math.max(0, Math.min(100, this.readinessPercent));
    return this.donutCircumference() * (1 - pct / 100);
  });

  /** Human-readable save indicator copy. Uses relativeNow as the
   *  reference clock so the calling shell can advance "Saved N min ago"
   *  with a low-cost ticker signal. */
  readonly saveStateLabel = computed(() => {
    switch (this.saveState) {
      case "saving":
        return "Saving…";
      case "dirty":
        return "Unsaved changes";
      case "saved":
        return this.lastSavedAt
          ? `Last updated ${formatRelative(this.relativeNow - this.lastSavedAt)}`
          : "Saved";
      case "idle":
      default:
        return "";
    }
  });

  // ── Click adapters ────────────────────────────────────────────────────────
  /** Coverage-card click — toggles between "show only this language"
   *  and "show all". Clicking the already-selected card returns to
   *  ALL so users have a one-click escape hatch without hunting for
   *  a separate "show all" button. */
  onLangCardClick(lang: ActiveLanguage): void {
    if (this.isLanguageSelected(lang)) {
      this.languageSelect.emit("ALL");
      return;
    }
    const code = (lang.shortCode || lang.label || "").toUpperCase();
    if (!code) return;
    this.languageSelect.emit(code);
  }

  /** True when the editor's active language mode already filters to
   *  this chip's locale (drives the chip's `--selected` visual and
   *  `aria-pressed`). Display codes from the language store
   *  ("MM"/"KH"/"TH"/"EN"/...) compare directly against the activeDisplay
   *  signal, which normalises the legacy "MY" alias to "MM". */
  isLanguageSelected(lang: ActiveLanguage): boolean {
    const active = this.activeDisplay();
    if (active === "ALL") return false;
    const code = (lang.shortCode || lang.label || "").toUpperCase();
    return active === code;
  }
}

/** Format an elapsed duration as a friendly "Nm ago" / "Ns ago" / "just now"
 *  string for the save indicator. */
function formatRelative(deltaMs: number): string {
  if (deltaMs < 0) return "just now";
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 15) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
